import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { applyWriteMap } from "../firebase/writes.js";
import {
  type AspectRecord,
  type EditableCharacter,
  addAspect,
  removeAspect,
  updateAspect,
} from "../lib/character-edits.js";
import AspectFormModal, { type AspectFormValues } from "./AspectFormModal.js";

interface Props {
  characterPath: string;
  character: EditableCharacter;
}

export default function CharacterAspectsEditor({ characterPath, character }: Props): ReactElement {
  const { t } = useTranslation();
  const [modal, setModal] = useState<
    { mode: "create" } | { mode: "edit"; aspect: AspectRecord } | null
  >(null);

  async function handleSubmit(values: AspectFormValues): Promise<void> {
    const { db } = getFirebase();
    if (modal?.mode === "create") {
      await applyWriteMap(db, addAspect(characterPath, character, values));
    } else if (modal?.mode === "edit") {
      await applyWriteMap(db, updateAspect(characterPath, character, modal.aspect.id, values));
    }
    setModal(null);
  }

  async function handleDelete(id: string): Promise<void> {
    if (!window.confirm(t("edit.confirm.delete-aspect"))) return;
    const { db } = getFirebase();
    await applyWriteMap(db, removeAspect(characterPath, character, id));
  }

  return (
    <section>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase text-muted-foreground">
          {t("sheet.aspects")}
        </h2>
        <button
          type="button"
          className="rounded border px-2 py-0.5 text-xs"
          onClick={() => setModal({ mode: "create" })}
        >
          {t("edit.aspect.add")}
        </button>
      </div>

      <ul className="mt-2 space-y-1">
        {(character.aspects ?? []).map((a) => (
          <li key={a.id} className="flex items-center gap-2 text-sm">
            <span className="flex-1">
              <span className="font-medium">{a.text}</span>{" "}
              <span className="text-xs text-muted-foreground">
                · {t(`edit.aspect.kind.${a.kind}`)} · {t(`edit.aspect.lifetime.${a.lifetime}`)}
                {a.visibility === "hidden" ? ` · ${t("aspect.hidden")}` : ""}
              </span>
            </span>
            <button
              type="button"
              className="rounded border px-2 py-0.5 text-xs"
              onClick={() => setModal({ mode: "edit", aspect: a })}
            >
              {t("edit.edit")}
            </button>
            <button
              type="button"
              className="rounded border border-rose-400 px-2 py-0.5 text-xs text-rose-700"
              onClick={() => handleDelete(a.id)}
            >
              {t("edit.delete")}
            </button>
          </li>
        ))}
      </ul>

      <AspectFormModal
        open={modal !== null}
        mode={modal?.mode ?? "create"}
        {...(modal?.mode === "edit"
          ? {
              initial: {
                text: modal.aspect.text,
                kind: modal.aspect.kind,
                visibility: modal.aspect.visibility,
                lifetime: modal.aspect.lifetime,
                scope: modal.aspect.scope,
              },
            }
          : {})}
        onSubmit={handleSubmit}
        onCancel={() => setModal(null)}
      />
    </section>
  );
}
